import type {
  GraphType,
  GroupByColumn,
  MeasuringMode,
} from "@hightouch/lib/query/visual/types/analytics";
import isObject from "lodash/isObject";
import type { MetricSelectorOptions } from "src/components/analytics/shared/metric-selector/types";
import type { PersonalizationValue } from "src/hooks/use-personalization";

import type { AggregationOption } from "src/pages/metrics/constants";
import type {
  AnalyticsFrequency,
  AndOrCondition,
  AttributionWindow,
  AudienceAggregationType,
  ColumnType,
  FilterableColumn,
  IntervalUnit,
  NormalizationType,
  PerUserAggregationType,
  PropertyCondition,
  RawColumn,
  RelatedColumn,
  Relationship,
  NestableCondition,
  SyntheticColumn,
  TraitDefinition,
  TransformedColumn,
  UserDefinedMetricConfig,
  VisualQueryFilter,
} from "src/types/visual";
import { SyntheticMetricType } from "src/types/visual";
import type { CohortType, SavedAnalyticsChartQuery } from "src/graphql";
export type { GroupByColumn };

export enum TimeOptions {
  SevenDays = "7-days",
  FourteenDays = "14-days",
  ThirtyDays = "30-days",
  SixtyDays = "60-days",
  NinetyDays = "90-days",
  Custom = "custom",
}

export enum MeasurementScope {
  AllData = "allData",
  DecisionEngineFlow = "decisionEngineFlow",
}

export enum ChartTab {
  Insights = "insights",
  Funnel = "funnel",
  SplitsMeasurement = "splitsMeasurement",
}

export enum ChartVariant {
  Full = "full",
  Embedded = "embedded",
}

export type CombinedChartType =
  | GraphType
  | ChartTab.Funnel
  | ChartTab.SplitsMeasurement;

export type AudienceSplitInformation = {
  id: string;
  name: string;
  label?: string;
  enabled: boolean;
  isHoldoutGroup: boolean;
  percentage: number;
  firstSyncAt?: string;
  destinations?: {
    icon?: string;
    name?: string;
  }[];
  text?: string;
};

export type BaseAudience = {
  type?: CohortType;
  id: number;
  name: string;
  splits: AudienceSplitInformation[];
  subsetIds?: string[]; // For parent models
};

// Temp audiences that filters the parent model that are meant to just exist in the chart
export type AdHocAudience = BaseAudience & {
  filter: VisualQueryFilter;
  subsetIds?: string[];
};

export type SelectedAudience = AdHocAudience | BaseAudience;

export const isAdHocAudience = (
  audience: SelectedAudience,
): audience is AdHocAudience => {
  return (
    isObject(audience) &&
    "filter" in audience &&
    audience.filter?.conditions !== undefined // Empty conditions are valid for ad-hoc audiences
  );
};

export type ParentModelOption = {
  label: string;
  value: number;
  connection: {
    __typename?: "connections";
    name: string;
    definition: {
      __typename?: "SourceDefinition";
      name: string;
      icon: string;
    };
  } | null;
  disabled: boolean;
};

export type EventOrMetricOption = {
  id: string; // this is relationship id for events
  // Traits are represented as a column, but we want to be able to show them
  // as the selected trait in the UI
  traitId?: string;
  eventModelId: any;
  modelId?: string | null;
  name: string;
  description: string | null;
  relationshipId?: string;
  campaignIdColumn?: string | null;
  eventType?: string | null;
  // If synthetic metric type
  source?: SyntheticMetricType;
  resourceId?: string;
};

export type MetricGroupOption = {
  label: string;
  options: EventOrMetricOption[];
};

export type MetricSelectionColumn = Pick<
  FilterableColumn,
  | "model_id"
  | "model_name"
  | "column_reference"
  | "name"
  | "description"
  | "alias"
  | "type"
  | "custom_type"
>;

export type MetricSelection = {
  id: string; // this is relationship id for events
  traitId?: string;
  eventModelId: string | null;
  modelId?: string | null;
  name: string;
  description: string | null;
  aggregationMethod: AggregationOption;
  conditions: AndOrCondition<NestableCondition>[];
  attributionWindow?: AttributionWindow;
  column?: MetricSelectionColumn;
  normalization?: NormalizationType;
  relationshipId?: string;
  campaignIdColumn?: string | null;
  eventType?: string | null;
  // If synthetic metric type
  source?: SyntheticMetricType;
  resourceId?: string;
  cacheKey?: string;
};

/**
 * Saved metrics have unique database IDs and store their column reference
 * within the metric definition (not on MetricSelection). Ad-hoc metrics
 * (event/model) set eventModelId or modelId and share a relationship ID.
 */
export const isSavedMetric = (
  metric: Pick<MetricSelection, "id" | "eventModelId" | "modelId">,
): boolean => {
  return Boolean(metric.id) && !metric.eventModelId && !metric.modelId;
};

export const isSyntheticMetric = (
  metric: EventOrMetricOption | MetricSelection | undefined,
) => {
  return (
    metric &&
    metric.source &&
    Object.values(SyntheticMetricType).includes(
      metric.source as SyntheticMetricType,
    )
  );
};

export type FilterEventColumn = {
  name: string;
  type: string;
  // only supported property condition column types
  columnReference:
    | RawColumn
    | RelatedColumn
    | TransformedColumn
    | SyntheticColumn;
  groupLabel: string; // to distinguish merge columns and other measurement scopes
  description?: string;
};

export type MeasuringSelection = {
  id: string;
  scope: MeasurementScope;
};

// Generic column type for different measuring selection scopes
export type MeasuringSelectionColumn = {
  alias: string;
  columnType: ColumnType;
  columnReference: SyntheticColumn;
  description?: string;
};

export type MeasurementColumnOptions = {
  label: string;
  options: MeasuringSelectionColumn[];
};

const requiredMetricSelectionKeys = [
  "aggregationMethod",
  "conditions",
  "eventModelId",
  "id",
  "name",
];

const eitherOrRequiredMetricSelectionKeys = ["eventModelId", "modelId"];

export const isMetricSelection = (
  metricSelection: MetricSelection,
): metricSelection is MetricSelection => {
  let isMetricSelection = true;

  const keys = new Set(Object.keys(metricSelection));

  requiredMetricSelectionKeys.forEach((key) => {
    if (!keys.has(key)) {
      isMetricSelection = false;
    }
  });

  // MetricSelections require either eventModelId or modelId to be present
  const hasEitherOrRequiredKeys = eitherOrRequiredMetricSelectionKeys.some(
    (key) => keys.has(key),
  );

  if (!hasEitherOrRequiredKeys) {
    isMetricSelection = false;
  }

  return isMetricSelection;
};

export type SavedChartFunnelStage = {
  event_model_id: string;
  relationship_id: string;
  funnel_stage_definition?: {
    subconditions: AndOrCondition<PropertyCondition>[];
  };
};

export type FunnelStep = {
  id: string; // This is specific to the frontend so that the stepper works correctly.
  eventModelId: string;
  subconditions: AndOrCondition<PropertyCondition>[];
  relationshipId: string;
};

export type FunnelStepGraphDataPoint = {
  /* The grouping value */
  groupByValue: string | number | undefined;

  /* The amount of users in the stage */
  count: number;

  /* The conversion value between the previous and current step */
  conversion: number;

  /* 
    The size of the conversion bar to show in the bar chart.
    May not be the same value as the conversion
  */
  conversionBarSize: number;
  /*
    The size of the drop off bar to show in the bar chart.
    This bar will be stacked on top of conversionBar.
  */
  dropOffBarSize: number;
};

export type FunnelStepGraphData = {
  stageName: string;
  eventModelId: string;
  relationshipId: string;
  subconditions: AndOrCondition<PropertyCondition>[];
  index: number; // redundant but necessary for recharts graph
  data: Record<string, FunnelStepGraphDataPoint>;
};

export type FunnelTableData = {
  seriesName: string;
  conversion: number;
  steps: {
    stageName: string;
    numberOfUsers: number;
    conversion: number;
  }[];
};

export enum FunnelMeasurementType {
  UniqueConversion = "uniqueConversion",
  TotalConversion = "totalConversion",
}

export type HoveredSection = {
  dataKey: string | null;
  seriesName: string | null;
  section: "conversion" | "dropOff";
  index: number | undefined;
};

export type ConversionWindow = {
  quantity: number;
  unit: IntervalUnit.Day | IntervalUnit.Week | IntervalUnit.Month;
};

export type ConversionCriteriaData = {
  measuringType: FunnelMeasurementType;
  funnelConversionWindow: ConversionWindow;
};

export type GroupByOptionColumnReference = GroupByColumn[] | GroupByColumn;

export type GroupByOption = {
  groupLabel: string | null;
  name: string;
  columnReference: GroupByOptionColumnReference;
  type: ColumnType;
};

export type NormalizationOption = {
  value: NormalizationType;
  label: string;
  description: string;
};

export type GroupByValue = Array<GroupByColumn | GroupByColumn[] | undefined>;

export type ParentModel = {
  id: number;
  name: string;
  filterable_audience_columns: FilterableColumn[];
  relationships: Relationship[];
  sampled_model_available: boolean;
  connection?: {
    id: string;
    name: string;
    audience_snapshotting_enabled?: boolean;
    definition: { icon: string; type: string; name: string };
  } | null;
};

export type Metric = {
  id: string;
  name: string;
  description: string | null;
  aggregation: PerUserAggregationType;
  audienceAggregation: AudienceAggregationType;
  attribution_window: AttributionWindow;
  // The analytics page assumes that only "User-defined" metrics are being queried
  // as opposed to "Predefined" metrics which have a different config definition.
  config: UserDefinedMetricConfig;
  audiences: {
    enabled: boolean;
    segment: {
      id: any;
      name: string;
    };
  }[];
  normalization?: NormalizationType;
};

export type MeasurementGroupOption = {
  groupLabel: string;
  options: {
    id: string;
    label: string;
    scope: MeasurementScope;
    icon: () => JSX.Element;
  }[];
};

export type SyntheticColumnValuesGetter = (
  col: SyntheticColumn,
) => Array<{ value: string; label: string }>;

export type SelectedAudienceWithBaseline = {
  cohort: SelectedAudience;
  baselineGroup?: string;
};

export type SubsetCategoryOption = {
  id: string;
  name: string;
  description: string | null;
  is_required: boolean;
  subset_values: Array<{
    id: string;
    name: string;
    v2_grants: {
      user_group_id: any | null;
    }[];
  }>;
};

export type ExperimentsFormState = {
  // Our new way of representing audiences
  cohorts?: SelectedAudienceWithBaseline[];

  // Experiments related global fields that apply to all cohorts and metrics
  globalCohortFilter?: VisualQueryFilter;
  globalMeasurementWindow?: AttributionWindow;
  globalNormalization?: NormalizationType;
};

export type ChartFormState = {
  parentModelId: number | null;
  name?: string;
  description?: string | null;
  cumulative: boolean;
  funnelConversionWindow: ConversionWindow;
  funnelSteps: FunnelStep[];
  chartTab: ChartTab; // Insights or Funnel
  graphType?: GraphType; // Within insights, the types of graph to display (line, bar, table)
  measuringSelection: MeasuringSelection | undefined;
  measuringMode?: MeasuringMode | undefined;
  groupByColumns: (GroupByColumn | undefined)[];
  funnelMeasuringType: FunnelMeasurementType;
  metricSelection: MetricSelection[];
  rollupFrequency: AnalyticsFrequency;
  selectedAudiences: SelectedAudience[] | undefined;
  selectedDates: string[]; // Dates get serialized as ISO strings due to URL encoding
  timeValue: TimeOptions;
  addToDashboardOptions?: {
    dashboard: { name: string; id: string };
  };
} & ExperimentsFormState;

export type ChartSchema = {
  dependenciesLoading: boolean;
  audiences: SelectedAudience[] | undefined;
  metrics: Metric[];
  events: Relationship[];
  traits: TraitDefinition[];
  parent: ParentModel | null;
  relatedModels: Relationship[];
  measurementsOptions?: MeasurementGroupOption[];
  measurementColumnOptions?: MeasurementColumnOptions;
  additionalUserColumns?: MeasuringSelectionColumn[];
  parentModelLoading: boolean;
  parentModelOptions: ParentModelOption[];
  parentModelOptionsLoading: boolean;
  measurementsOptionsLoading?: boolean;
  measurementColumnsLoading?: boolean;
  measuringMetricResources?: Record<string, unknown>;
  subsetCategoryOptions?: SubsetCategoryOption[];
  syntheticColumnValuesGetter?: SyntheticColumnValuesGetter;
  metricSelectorOptions?: MetricSelectorOptions;
  fastQueryPreference?: PersonalizationValue<boolean>;
  isArchived?: boolean;
  isSavedView?: boolean;
};

type ReportSchema = {
  savedChartId?: string;
  reportSchedule?: NonNullable<
    SavedAnalyticsChartQuery["saved_analytics_charts_by_pk"]
  >["analytics_report"];
};

export type AnalyticsState = ChartFormState & ChartSchema;

export type AnalyticsActions = {
  addGroupByColumn: (column: GroupByColumn | undefined, index: number) => void;
  addGroupByColumns: (
    column: (GroupByColumn | undefined)[],
    fromIndex: number,
  ) => void;
  removeGroupByColumns: (startIndex: number, endIndex?: number) => void;
  resetView: (chartTab: ChartTab) => void;
  selectParentModel: (id: number, name?: string) => void;
  applySavedParentModel: (id: number, name?: string) => void;
  setSelectedDates: (dates: Date[]) => void;
  setLookbackWindow: (time: TimeOptions) => void;
  selectMeasurement: (measuringSelection: MeasuringSelection) => void;
  selectMeasurementMode: (measuringMode: MeasuringMode) => void;
  setChartTab: (tab: ChartTab) => void;
  setGlobalMeasurementWindow: (
    measurementWindow: AttributionWindow | undefined,
  ) => void;
  setGlobalCohortFilter: (cohortFilter: VisualQueryFilter | undefined) => void;
  setGlobalNormalization: (
    normalization: NormalizationType | undefined,
  ) => void;
};

export type AnalyticsContextType = ChartSchema &
  ReportSchema &
  AnalyticsActions;
